import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Alert, Avatar, Chip, IconButton,
  Select, MenuItem, InputAdornment
} from '@mui/material';
import { Users as UsersIcon, Pencil, Search } from 'lucide-react';
import AdminModal from './AdminModal';
import { AdminPanelSkeleton, useCachedPanelCount } from './adminPanelContent';
import { API_BASE_URL } from '../utils/api';

const cardSx = {
  p: 1.5,
  borderRadius: '8px',
  border: '1px solid rgba(255,255,255,0.08)',
  bgcolor: 'rgba(255,255,255,0.02)',
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  transition: 'all 0.12s ease',
  '&:hover': { bgcolor: 'rgba(139,92,246,0.06)', borderColor: 'rgba(139,92,246,0.25)' }
};

export default function UsersPanel({ getToken }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cachedCount, setCachedCount] = useCachedPanelCount('users:list', 6);
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState(null);
  const [role, setRole] = useState('');
  const [instruction, setInstruction] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getToken]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/users`, { headers: h });
      if (!res.ok) throw new Error('Failed to load users');
      const data = await res.json();
      setUsers(data || []);
      setCachedCount((data || []).length);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, setCachedCount]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const email = (u.email || '').toLowerCase();
      const un = (u.username || '').toLowerCase();
      const name = `${u.displayName || u.firstName || ''} ${u.lastName || ''}`.trim().toLowerCase();
      return email.includes(q) || un.includes(q) || name.includes(q);
    });
  }, [users, search]);

  const openEdit = (user) => {
    setEditing(user);
    setRole(user.role || '');
    setInstruction(user.instruction || '');
  };
  const closeEdit = () => {
    setEditing(null);
    setRole('');
    setInstruction('');
    setSaving(false);
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      setError('');
      const h = await authHeaders();
      const [roleRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/users/${editing.id}/role`, {
          method: 'POST', headers: h, body: JSON.stringify({ role })
        }),
        fetch(`${API_BASE_URL}/api/users/${editing.id}/instruction`, {
          method: 'PATCH', headers: h, body: JSON.stringify({ instruction })
        })
      ]);
      if (!roleRes.ok) throw new Error('Failed to update user role');
      setUsers((prev) => prev.map((u) => (u.id === editing.id ? { ...u, role: role || null, instruction } : u)));
      closeEdit();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const label = (u) => u?.displayName || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.username || '—';

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Box sx={{ color: 'secondary.light', display: 'flex', alignItems: 'center' }}>
          <UsersIcon size={22} strokeWidth={2} />
        </Box>
        <Typography variant="h5" fontWeight={600}>Users</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {users.length >= 10 && (
        <TextField
          size="small"
          fullWidth
          placeholder="Search by @username, email, or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} strokeWidth={2} style={{ opacity: 0.55 }} />
              </InputAdornment>
            )
          }}
        />
      )}

      {loading ? (
        <AdminPanelSkeleton variant="list" count={cachedCount} />
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
          {users.length === 0 ? 'No users found' : 'No users match your search.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.25 }}>
          {filtered.map((u) => (
            <Box key={u.id} sx={cardSx}>
              {u.picture
                ? <Avatar src={u.picture} sx={{ width: 36, height: 36 }} />
                : <Avatar sx={{ width: 36, height: 36 }}>{(label(u) || '?').slice(0, 1).toUpperCase()}</Avatar>}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{label(u)}</Typography>
                {u.username && <Typography variant="caption" color="text.secondary" noWrap display="block">@{u.username}</Typography>}
              </Box>
              <Chip
                label={u.role || 'No role'}
                size="small"
                color={u.role === 'admin' ? 'primary' : 'default'}
                sx={{ flexShrink: 0 }}
              />
              <IconButton size="small" onClick={() => openEdit(u)} sx={{ color: 'text.secondary' }}>
                <Pencil size={14} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <AdminModal
        open={!!editing}
        onClose={closeEdit}
        disableClose={saving}
        maxWidth="xs"
        title="Manage user"
        actions={
          <>
            <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {editing?.picture
              ? <Avatar src={editing.picture} sx={{ width: 40, height: 40 }} />
              : <Avatar sx={{ width: 40, height: 40 }}>{(label(editing) || '?').slice(0, 1).toUpperCase()}</Avatar>}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>{label(editing)}</Typography>
              {editing?.username && (
                <Typography variant="caption" color="text.secondary">@{editing.username}</Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Role</Typography>
            <Select size="small" value={role} onChange={(e) => setRole(e.target.value)} disabled={saving} displayEmpty>
              <MenuItem value=""><em>No role</em></MenuItem>
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Instruction</Typography>
            <TextField
              multiline
              fullWidth
              minRows={2}
              maxRows={8}
              placeholder="Name, preferred address, team role, communication style"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              disabled={saving}
              size="small"
            />
          </Box>
        </Box>
      </AdminModal>
    </Box>
  );
}
